import * as languagesKeyset from 'i18n/Language';
import * as regionsKeyset from 'i18n/Region';

import { Example } from './model/common';
import { UpdateOAuthAppSettings } from './model/oauth';
import { Operation } from './model/operation';
import { SkillAccess } from './model/settings';
import { Channel, Skill } from './model/skill';
import { Timezone } from './model/time';
import { UserAgreementOrDraft } from './model/userAgreement';

// todo: why not use standard Omit?
export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export type EmailConfirmationStage = 'pending' | 'success' | 'failure' | 'not-modified';

export type UnsubscribeState = 'pending' | 'success' | 'failure';

export interface Snapshot {
    skills: Skill[];
    timezones: Timezone[];
    operations: Operation[];
    categories: AliceSkillCategory[];
    /**
     * Alice Apps Store categories. Optional so older snapshot payloads and test fixtures
     * without the field still type-check; the forms treat a missing list as empty.
     */
    aliceAgentAppCategories?: AliceAgentAppCategory[];
    maxSkillsPerUser: number;
    voices: TtsVoice[];
    regionsWithSufficientLanguages: RegionSufficientLanguage[];
}

export interface AppsMetaInfo {
    installable_apps: InstallableAppsMeta;
}

export interface InstallableAppsMeta {
    maxAppsPerUser: number;
    targetPlatformsByChannel: { [key in Channel]?: string[] };
    categories: AliceSkillCategory[];
    ageRestrictions: string[];
}

export interface TtsVoice {
    name: string;
    title: string;
    samples: Array<{
        src: string;
        type: string;
    }>;
}

interface AliceSkillCategory {
    type: string;
    title: string;
    isDefault: boolean;
}

/** Global Alice Apps Store category from `/snapshot` (`result.aliceAgentAppCategories`). */
export interface AliceAgentAppCategory {
    name: string;
    title: Record<string, string>;
}

interface CommonFormSettings {
    description: string;
    category: string;
    brandVerificationWebsite: string;
    developerName: string;
    logoId: string | null;
    logoUrl: string | null;
    name: string;
    email: string;
    hideInStore: boolean;
}

export type BackendType = 'webhook' | 'function';

export interface AliceSkillFormSettings extends CommonFormSettings {
    activationPhrases: string[];
    backendType: BackendType;
    uri: string;
    functionId: string;
    structuredExamples: Example[];
    skillAccess?: SkillAccess;
    noteForModerator: string;
    requiredInterfaces: string[];
    exactSurfaces: string[];
    surfaceWhitelist: string[];
    surfaceBlacklist: string[];
    voice: string;
    yaCloudGrant: boolean;
    explicitContent: boolean;
    oauthAppId: string;
    appMetricaApiKey: string;
    useStateStorage: boolean;
    rsyPlatformId: string;
    skillUserAgreements: UserAgreementOrDraft[];
}

export type AliceSkillFormSettingsField = keyof AliceSkillFormSettings;

export type LanguageCode = keyof typeof languagesKeyset.en;
export type RegionCode = keyof typeof regionsKeyset.en;

export interface SupportedLanguageFormData {
    languageCode?: LanguageCode;
    languageDisplayName?: string;
    appName?: string;
    subtitle?: string;
    description?: string;

    externalSettingsDescription?: string;
    supportedUnitsDescription?: string;
    supportServiceUrl?: string;
    supportServiceUrlIsRequired?: boolean;
}

export interface RegionSufficientLanguage {
    regionCode: RegionCode;
    sufficientLanguageCodes: LanguageCode[];
}

export type ErrorForLanguageForm = {
    [Property in keyof SupportedLanguageFormData]?: string;
};

export interface SmartHomeFormSettings extends CommonFormSettings {
    secondaryTitle: string;

    backendType: BackendType;
    uri: string;
    functionId: string;
    noteForModerator: string;
    voice: string;
    oauthAppId: string;
    androidDeepLink: string;
    iosDeepLink: string;
    iosDeepLinkFallbackURL: string;
    isTrustedSmartHomeSkill: boolean;
    skillAccess?: SkillAccess;
    supportedLanguages: SupportedLanguageFormData[];
    enableAllAvailableRegions: boolean;
    selectedRegions: RegionCode[];
    skillUserAgreements: UserAgreementOrDraft[];
}

export type AgeRestriction = '0+' | '6+' | '12+' | '16+' | '18+';

export interface InstallableAppFormSettings extends CommonFormSettings {
    activationPhrases: string[];
    backgroundImageId?: string;
    backgroundImageUrl?: string;
    packageName: string;
    noteForModerator: string;
    brandIsVerified: boolean;
    emailIsVerified: boolean;
    isTrusted: boolean;
    hasMonetization: boolean;
    ageRestriction?: AgeRestriction;
    apkInfosWithTargets: ApkInfoWithTargets[];
    multiApkEnabled: boolean;
    secondaryTitle: string;
    eulaAgreement?: LicenseAgreement;
    privacyAgreement?: LicenseAgreement;
    screenshots?: InstallableAppScreenshot[];
    skillAccess: SkillAccess;
    secondaryLogoId: string | null;
    secondaryLogoUrl: string | null;
    squareLogoId?: string;
    squareLogoUrl?: string;
    voiceControlEnable: boolean;
    isAdvancedVoiceControlSupported: boolean;
}

export type LicenseAgreement = {
    name: string;
    url: string;
};

export type InstallableAppScreenshot = {
    imageUrl: string;
    imageId: string;
};

export type ApkInfoWithTargets = {
    appVersionId?: string;
    packageName?: string;
    url?: string;
    versionNotes?: string;
    versionCode?: number;
    versionName?: string;
    minSdkVersion?: number;
    androidGroup?: number;
    targetPlatforms?: string[];
    _creationId: string;
};

export type SmartHomeFormSettingsField = keyof SmartHomeFormSettings;

export type UpdateOAuthAppSettingsField = keyof UpdateOAuthAppSettings;

export type AnyFormSettings = AliceSkillFormSettings | SmartHomeFormSettings | InstallableAppFormSettings;

export enum PhraseCommonness {
    Green = 'green',
    Red = 'red',
}

export interface FeatureFlags {
    skill_feature_flags: string[];
    user_feature_flags: Record<string, boolean>;
}
